import { Plus, Save } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";

import { useAppSettings } from "@/components/app-settings/app-settings-provider";
import type { TermRow } from "@/components/settings/settings-types";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { apiGet, apiPatch, apiPost } from "@/lib/client/api";

type TermsResponse = {
  terms: TermRow[];
};

type TermForm = {
  code: string;
  titleEn: string;
  titleZh: string;
  bodyEn: string;
  bodyZh: string;
  sortOrder: number;
  active: boolean;
};

const emptyTermForm: TermForm = {
  code: "",
  titleEn: "",
  titleZh: "",
  bodyEn: "",
  bodyZh: "",
  sortOrder: 100,
  active: true,
};

export function TermsSettings({ refreshToken = 0 }: { refreshToken?: number }) {
  const { language } = useAppSettings();
  const text = language === "zh" ? zh : en;
  const [terms, setTerms] = useState<TermRow[]>([]);
  const [drafts, setDrafts] = useState<Record<string, TermForm>>({});
  const [form, setForm] = useState<TermForm>(emptyTermForm);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function loadTerms() {
    setLoading(true);
    setError(null);

    try {
      const response = await apiGet<TermsResponse>("/api/settings/terms");
      setTerms(response.terms);
      setDrafts(
        Object.fromEntries(
          response.terms.map((term) => [
            term.id,
            {
              code: term.code,
              titleEn: term.titleEn,
              titleZh: term.titleZh,
              bodyEn: term.bodyEn,
              bodyZh: term.bodyZh,
              sortOrder: term.sortOrder,
              active: term.active,
            },
          ]),
        ),
      );
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : text.loadError);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadTerms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, refreshToken]);

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setCreating(true);
    setError(null);
    setMessage(null);

    try {
      await apiPost<TermForm, { id: string }>("/api/settings/terms", form);
      setForm(emptyTermForm);
      setMessage(text.created);
      await loadTerms();
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : text.createError);
    } finally {
      setCreating(false);
    }
  }

  async function handleSave(termId: string) {
    const draft = drafts[termId];

    if (!draft) return;

    setSavingId(termId);
    setError(null);
    setMessage(null);

    try {
      await apiPatch<Omit<TermForm, "code">, { ok: true }>(`/api/settings/terms/${termId}`, {
        titleEn: draft.titleEn,
        titleZh: draft.titleZh,
        bodyEn: draft.bodyEn,
        bodyZh: draft.bodyZh,
        sortOrder: Number(draft.sortOrder),
        active: draft.active,
      });
      setMessage(text.saved);
      await loadTerms();
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : text.saveError);
    } finally {
      setSavingId(null);
    }
  }

  return (
    <section className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">{text.title}</h1>
        <p className="text-sm text-muted-foreground">{text.description}</p>
      </header>

      <StatusMessage message={message} error={error} />

      <Card className="p-4">
        <form className="space-y-3" onSubmit={handleCreate}>
          <div className="grid gap-3 md:grid-cols-[1fr_1fr_1fr_120px_120px]">
            <Field label={text.code} value={form.code} onChange={(value) => setForm({ ...form, code: value })} />
            <Field label={text.titleEn} value={form.titleEn} onChange={(value) => setForm({ ...form, titleEn: value })} />
            <Field label={text.titleZh} value={form.titleZh} onChange={(value) => setForm({ ...form, titleZh: value })} />
            <NumberField label={text.sortOrder} value={form.sortOrder} onChange={(value) => setForm({ ...form, sortOrder: value })} />
            <ToggleField label={text.active} checked={form.active} onChange={(checked) => setForm({ ...form, active: checked })} />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <BodyField label={text.bodyEn} value={form.bodyEn} onChange={(value) => setForm({ ...form, bodyEn: value })} />
            <BodyField label={text.bodyZh} value={form.bodyZh} onChange={(value) => setForm({ ...form, bodyZh: value })} />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={creating}>
              <Plus className="h-4 w-4" aria-hidden="true" />
              {creating ? text.creating : text.create}
            </Button>
          </div>
        </form>
      </Card>

      {loading ? <Card className="p-4 text-sm text-muted-foreground">{text.loading}</Card> : null}
      {!loading && terms.length === 0 ? <Card className="p-4 text-sm text-muted-foreground">{text.empty}</Card> : null}
      {!loading
        ? terms.map((term) => {
            const draft = drafts[term.id];

            if (!draft) return null;

            return (
              <Card key={term.id} className="p-4">
                <div className="grid gap-3 md:grid-cols-[1fr_1fr_120px_120px_auto]">
                  <Field label={text.titleEn} value={draft.titleEn} onChange={(value) => updateDraft(term.id, { titleEn: value })} />
                  <Field label={text.titleZh} value={draft.titleZh} onChange={(value) => updateDraft(term.id, { titleZh: value })} />
                  <NumberField label={text.sortOrder} value={draft.sortOrder} onChange={(value) => updateDraft(term.id, { sortOrder: value })} />
                  <ToggleField label={text.active} checked={draft.active} onChange={(checked) => updateDraft(term.id, { active: checked })} />
                  <Button type="button" variant="secondary" className="mt-5" onClick={() => void handleSave(term.id)} disabled={savingId === term.id}>
                    <Save className="h-4 w-4" aria-hidden="true" />
                    {savingId === term.id ? text.saving : text.save}
                  </Button>
                </div>
                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <BodyField label={text.bodyEn} value={draft.bodyEn} onChange={(value) => updateDraft(term.id, { bodyEn: value })} />
                  <BodyField label={text.bodyZh} value={draft.bodyZh} onChange={(value) => updateDraft(term.id, { bodyZh: value })} />
                </div>
              </Card>
            );
          })
        : null}
    </section>
  );

  function updateDraft(termId: string, patch: Partial<TermForm>) {
    setDrafts((current) => ({
      ...current,
      [termId]: {
        ...(current[termId] ?? emptyTermForm),
        ...patch,
      },
    }));
  }
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      <Input value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function NumberField({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      <Input type="number" value={value} onChange={(event) => onChange(Number(event.target.value))} />
    </label>
  );
}

function ToggleField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <label className="mt-5 flex h-9 items-center gap-2 rounded-md border border-border px-3 text-sm">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="h-4 w-4 accent-primary" />
      {label}
    </label>
  );
}

function BodyField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-muted-foreground">{label}</span>
      <Textarea value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function StatusMessage({ message, error }: { message: string | null; error: string | null }) {
  if (!message && !error) return null;

  return (
    <div className={error ? "rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive" : "rounded-md border border-primary/30 bg-primary/10 px-3 py-2 text-sm text-primary"}>
      {error ?? message}
    </div>
  );
}

const en = {
  title: "Terms",
  description: "Reusable bilingual payment, validity, scope, and usage terms.",
  code: "Code",
  titleEn: "English title",
  titleZh: "Chinese title",
  bodyEn: "English body",
  bodyZh: "Chinese body",
  sortOrder: "Order",
  active: "Active",
  create: "Create term",
  creating: "Creating...",
  save: "Save",
  saving: "Saving...",
  loading: "Loading terms...",
  empty: "No terms yet.",
  created: "Term created.",
  saved: "Term saved.",
  loadError: "Unable to load terms.",
  createError: "Unable to create term.",
  saveError: "Unable to save term.",
};

const zh: typeof en = {
  title: "条款",
  description: "可复用的双语付款、有效期、范围和授权条款。",
  code: "代码",
  titleEn: "英文标题",
  titleZh: "中文标题",
  bodyEn: "英文内容",
  bodyZh: "中文内容",
  sortOrder: "排序",
  active: "启用",
  create: "创建条款",
  creating: "创建中...",
  save: "保存",
  saving: "保存中...",
  loading: "正在加载条款...",
  empty: "暂无条款。",
  created: "条款已创建。",
  saved: "条款已保存。",
  loadError: "无法加载条款。",
  createError: "无法创建条款。",
  saveError: "无法保存条款。",
};
